// The hosted Ella chat embed (chat.aheadhealth.com).
//
// Booking mounts NO widget of its own: the "Live chat" row calls openEllaChat(),
// which lazily injects the embed loader (`/ella/v1.js`) once and opens the panel.
// Config is passed via `window.ellaSettings` (the loader's SPA path — a
// dynamically-injected script has no `document.currentScript` to read data-ella-*
// attributes from). `launcher: false` = no bubble; the panel only opens from here.
// Membership-first on a desktop shows the embed's own bubble instead
// (showEllaLauncher): the loader then comes in at once, with `launcher: true`.
//
// Analytics ride along for free: the embed forwards its chat_* events over the
// bridge and the loader captures them into the host's `window.posthog` — booking's
// own PostHog — so booking + marketing chat land in one cross-surface funnel with
// no mapper on this side.
//
// Origin defaults to prod; override with VITE_ELLA_EMBED_ORIGIN to point a
// preview/staging build at a different host (must be an un-gated origin).

/** Where the visitor is in a multi-step flow and what they picked there, as the
 *  chat's server reads it (customer-support `booking_context`). */
export interface EllaBookingContext {
  flow: string;
  step?: string;
  /** The membership is bought. */
  paid?: boolean;
  /** What the visitor added on top of what the plan includes, as the chat's
   *  own identifiers (not the catalogue's). */
  added: string[];
}

interface EllaSettings {
  country: string;
  locale: string;
  scope: string;
  bookingContext?: EllaBookingContext;
  launcher?: boolean;
  /** false → the bubble stays round, never the labelled "Ask a question" pill. */
  launcherLabel?: boolean;
  /** Holds the bubble's teaser (its greeting and questions) until the host
   *  allows it (window.Ella.setTeaserAllowed). */
  teaserAfterScroll?: boolean;
  linksOrigin?: string;
  origin?: string;
}

declare global {
  interface Window {
    ellaSettings?: EllaSettings;
    Ella?: {
      open(): void;
      close(): void;
      identify(props: Record<string, unknown>): void;
      /** The market and language again: the loader reloads its frame. */
      setContext(country: string, locale: string): void;
      /** The visitor's place and basket in the flow: the chat reads it with each
       *  message. Call with nothing to clear it. Optional: the loader is served
       *  from the chat's own origin and deployed on its own, so a page can meet
       *  one that predates it. */
      setBookingContext?(bookingContext?: EllaBookingContext): void;
    };
  }
}

const EMBED_ORIGIN =
  import.meta.env.VITE_ELLA_EMBED_ORIGIN || "https://chat.aheadhealth.com";

let injected = false;
/** The latest context the flow declared, for a loader that is not here yet
 *  (it reads its settings once) and one still on its way. */
let bookingContext: EllaBookingContext | undefined;
/** An open asked for while the loader was still on its way. */
let openOnLoad = false;

const injectLoader = (settings: EllaSettings) => {
  injected = true;
  window.ellaSettings = settings;
  const script = document.createElement("script");
  script.src = `${EMBED_ORIGIN}/ella/v1.js`;
  script.async = true;
  // The loader defines window.Ella on load; open then, if asked meanwhile.
  script.addEventListener("load", () => {
    // A context declared while the loader was on its way, after its settings
    // were read.
    if (bookingContext) window.Ella?.setBookingContext?.(bookingContext);
    if (openOnLoad) window.Ella?.open();
  });
  document.body.appendChild(script);
};

/**
 * Open the Ella chat panel, injecting the embed loader on first use.
 *
 * `country` is the ISO market ("ch" | "de" | "nl"); `locale` is the short chat
 * language ("de" | "en" | "fr" | "nl"). The loader reads these once at load, so
 * the FIRST open fixes the panel's locale for the session (a language switch
 * mid-session then re-open keeps the first locale — acceptable; chat is rare and
 * late in the flow). window.Ella.open() buffers until the iframe is ready.
 */
export function openEllaChat(opts: { country: string; locale: string }): void {
  if (window.Ella) {
    window.Ella.open();
    return;
  }

  // Already injected but not yet ready → the load handler opens it.
  openOnLoad = true;
  if (!injected) {
    injectLoader({
      country: opts.country,
      locale: opts.locale,
      scope: "booking",
      bookingContext,
      launcher: false,
      origin: EMBED_ORIGIN,
    });
  }
}

/**
 * The embed's own bubble, bottom right, the panel opening from it: injects the
 * loader with `launcher: true` and opens nothing. A later openEllaChat() opens
 * the same panel. Only where the chat's server lets the page frame it (not
 * localhost), or the bubble is a blocked frame. The bubble alone: its teaser
 * stays held, as its questions would sit over the page's own buttons.
 */
export function showEllaLauncher(opts: {
  country: string;
  locale: string;
}): void {
  if (injected) return;
  injectLoader({
    country: opts.country,
    locale: opts.locale,
    scope: "booking",
    bookingContext,
    launcher: true,
    // Round only: the labelled pill sat beside Pay and read as a second
    // call to action (Laurent, 19.10).
    launcherLabel: false,
    teaserAfterScroll: true,
    origin: EMBED_ORIGIN,
  });
}

/**
 * Tells the chat where the visitor is in the flow and what they picked, on
 * every step and basket change; nothing clears it. Kept for a loader that is
 * not on the page yet, which then starts with it.
 */
export function setEllaBookingContext(next?: EllaBookingContext): void {
  bookingContext = next;
  window.Ella?.setBookingContext?.(next);
}
